import { ActivatedRoute } from '@angular/router';
import { OAuthProvider } from 'firebase/auth';
import { Directive, HostListener, OnInit, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { AuthService } from '@core/services/auth.service';
import { LoggingService } from '@core/services/logging.service';
import { CompanyInvite } from '@core/models/company-invite';
import { CompanyInviteService } from '@core/services/company-invite.service';
import { environment } from 'src/environments/environment';

/**
 * Attaches Microsoft sign-in behaviour to its host element. Clicking the host
 * triggers a Microsoft popup sign-in through {@link AuthService}, handles any
 * required account-linking flow, and renders error/account-link messages in the
 * page's `#OAuthError` element. Also redeems an `inviteCode` query param when
 * present.
 */
@Directive({
  selector: '[appMicrosoftSignin]',
  standalone: false,
})
export class MicrosoftSigninDirective implements OnInit {
  private authService = inject(AuthService);
  private loggingService = inject(LoggingService);
  private inviteService = inject(CompanyInviteService);
  private route = inject(ActivatedRoute);
  private platformId = inject(PLATFORM_ID);

  /** Cached reference to the `#OAuthError` element used to display messages. */
  authErrorElement?: HTMLElement;

  /**
   * Firebase provider ID → display label, in preference order. Used to describe
   * which existing sign-in method a user must use to link a new provider.
   */
  private static readonly PROVIDER_LABELS: ReadonlyArray<readonly [string, string]> = [
    ['google.com', 'Google'],
    ['microsoft.com', 'Microsoft'],
    ['password', 'email/password'],
  ];

  /**
   * Angular lifecycle hook. Redeems any invite code in the URL and surfaces a
   * pending account-link message left over from a prior redirect sign-in.
   * @returns void
   */
  ngOnInit(): void {
    this.checkInviteCode();
    // Surface any account-link message produced while completing a redirect
    // sign-in on this page load (see AuthService.processRedirectResult).
    this.showPendingOAuthMessage();
  }

  /**
   * Reads the `inviteCode` query parameter and, when valid, stores it in
   * `localStorage` (browser only) so the sign-up flow can redeem it later.
   * @returns void
   */
  private checkInviteCode() {
    this.route.queryParams.subscribe((params) => {
      const inviteCode = params['inviteCode'];
      if (inviteCode) {
        // Redeem the invite code
        this.inviteService.validateInviteCode(inviteCode).then((res) => {
          if (res) {
            this.inviteService
              .getDocument(inviteCode)
              .then((inviteCode: CompanyInvite) => {
                if (inviteCode) {
                  // Save the invite code to localStorage (browser only)
                  if (isPlatformBrowser(this.platformId)) {
                    localStorage.setItem('inviteCode', inviteCode.inviteCode);
                  }
                }
              });
          }
        });
      }
    });
  }

  /**
   * Host `click` handler. Suppresses the default navigation, clears any visible
   * auth error, builds a Microsoft `OAuthProvider` (tenant, prompt, and OIDC
   * scopes), and starts the sign-in.
   * @param event The DOM click event from the host element.
   * @returns The promise returned by {@link AuthLogin}.
   */
  @HostListener('click', ['$event'])
  onclick(event: Event) {
    event.preventDefault();
    event.stopPropagation();
    this.clearAuthError();

    const provider = new OAuthProvider('microsoft.com');
    provider.setCustomParameters({
      tenant: environment.microsoftAuth?.tenant || 'common',
      prompt: 'select_account',
    });
    provider.addScope('openid');
    provider.addScope('profile');
    provider.addScope('email');

    return this.AuthLogin(provider);
  }

  /**
   * Initiates Microsoft popup sign-in via {@link AuthService} and surfaces
   * provider-link or popup failures on the login form. Resolves on success or
   * when account linking is required; rejects with the provider error otherwise.
   * @param provider The {@link OAuthProvider} instance for Microsoft sign-in.
   * @returns A promise resolving with the sign-in result, or rejecting with the error.
   */
  AuthLogin(provider: OAuthProvider) {
    return this.authService
      .AuthLogin(provider)
      .then((res: any) => {
        if (res?.success) {
          return Promise.resolve(res);
        }

        // Second factor required: AuthService stashed the resolver and already
        // navigated to /mfa-challenge — this is a handled, non-error outcome.
        if (res?.mfaPending) {
          return Promise.resolve(res);
        }

        if (res?.accountLinkRequired) {
          this.showAccountLinkMessage(res, 'Microsoft');
          return Promise.resolve(res);
        }

        if (res?.success === false && res?.error) {
          return Promise.reject(res.error);
        }
        return Promise.reject(res);
      })
      .catch((error: any) => {
        this.reportAuthError(error);
        return Promise.reject(error);
      });
  }

  /**
   * Clears any text currently shown in the `#OAuthError` element. No-op during
   * server-side rendering.
   * @returns void
   */
  private clearAuthError(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.authErrorElement ??= document.getElementById('OAuthError') ?? undefined;
    if (this.authErrorElement) {
      this.authErrorElement.textContent = '';
    }
  }

  /**
   * Displays any account-link message stashed by a prior redirect sign-in
   * (consumed from {@link AuthService}). No-op during server-side rendering.
   * @returns void
   */
  private showPendingOAuthMessage(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const message = this.authService.consumeOAuthRedirectMessage();
    if (message) {
      this.authErrorElement ??= document.getElementById('OAuthError') ?? undefined;
      if (this.authErrorElement) {
        this.authErrorElement.textContent = message;
      }
    }
  }

  /**
   * Builds and renders a human-readable account-linking prompt telling the user
   * which existing provider they must sign in with first, and logs a warning.
   * @param result The sign-in result containing `email` and `existingProviders`.
   * @param pendingProviderLabel Display label of the provider being linked (e.g. `'Microsoft'`).
   * @returns void
   */
  private showAccountLinkMessage(result: any, pendingProviderLabel: string): void {
    const existingProvider = this.getPreferredProviderLabel(result?.existingProviders);
    const emailPrefix = result?.email ? `${result.email} already uses ` : 'This email already uses ';
    const message = `${emailPrefix}${existingProvider}. Sign in with ${existingProvider} to link your ${pendingProviderLabel} account.`;

    // Do not log the email itself (PII); record only its presence. The full
    // email is shown on-screen via `message`, never written to logs/telemetry.
    this.loggingService.logWarning('Microsoft sign-in requires account linking', {
      emailPresent: !!result?.email,
      existingProviders: result?.existingProviders,
      pendingProvider: pendingProviderLabel,
    });

    if (isPlatformBrowser(this.platformId)) {
      this.authErrorElement ??= document.getElementById('OAuthError') ?? undefined;
      if (this.authErrorElement) {
        this.authErrorElement.textContent = message;
      }
    }
  }

  /**
   * Maps a list of Firebase provider IDs to a friendly display label, preferring
   * Google, then Microsoft, then email/password, then the first entry. Matches
   * provider IDs by exact equality (via a Set) rather than substring checks.
   * @param providers The provider IDs linked to the account (e.g. `['microsoft.com']`).
   * @returns A user-facing provider label, or a generic fallback when none are given.
   */
  private getPreferredProviderLabel(providers?: string[]): string {
    if (!providers?.length) {
      return 'your existing sign-in method';
    }

    const providerIds = new Set(providers);
    for (const [id, label] of MicrosoftSigninDirective.PROVIDER_LABELS) {
      if (providerIds.has(id)) {
        return label;
      }
    }

    return providers[0];
  }

  /**
   * Normalizes a sign-in error, logs it, and renders a `code: message` string in
   * the `#OAuthError` element.
   * @param error The raw error thrown by the sign-in attempt.
   * @returns void
   */
  private reportAuthError(error: any): void {
    const normalizedError = this.normalizeAuthError(error);
    const message = `${normalizedError.code}: ${normalizedError.message}`;

    this.loggingService.logError('Microsoft sign-in failed', normalizedError);

    if (isPlatformBrowser(this.platformId)) {
      this.authErrorElement ??= document.getElementById('OAuthError') ?? undefined;
      if (this.authErrorElement) {
        this.authErrorElement.textContent = message;
      }
    }
  }

  /**
   * Unwraps a Firebase/auth error into a consistent shape with safe fallbacks
   * for the code and message.
   * @param error The raw error, optionally wrapping a Firebase error under `error`.
   * @returns An object with `code`, `message`, and optional `customData` and `name`.
   */
  private normalizeAuthError(error: any): {
    code: string;
    message: string;
    customData?: unknown;
    name?: string;
  } {
    const firebaseError = error?.error ?? error;

    return {
      code: firebaseError?.code ?? 'auth/unknown',
      message: firebaseError?.message ?? 'Microsoft sign-in failed.',
      customData: firebaseError?.customData,
      name: firebaseError?.name,
    };
  }
}
